
import React from 'react';
import { 
  ArrowLeft, 
  Building2, 
  Briefcase, 
  Scale, 
  TrendingUp, 
  Zap, 
  ShieldCheck, 
  Globe, 
  ArrowRight,
  Database,
  Users
} from 'lucide-react';

export const UseCasesView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const useCases = [
    {
      title: "Banks",
      icon: <Building2 className="text-blue-500" />,
      content: "Fraud detection via Threat Intelligence, real-time sanctions screening, AML/CTF monitoring, ESG Due Diligence (including supply chain), operational intelligence (Phone, IBAN, Email), HR/employee background checks, IP/Threat Intelligence monitoring, and Darkweb surveillance."
    },
    {
      title: "Private Equity & Venture Capital (PE/VC)",
      icon: <Briefcase className="text-indigo-500" />,
      content: "UBO mapping, ESG Due Diligence (including supply chain), Blockchain Intelligence, market sentiment & geopolitical risk analysis, compliance dashboards, executive verification, IP/Threat Intelligence monitoring, Darkweb monitoring, and Social Media eKYC for portfolio evaluation."
    },
    {
      title: "Law Enforcement & Regulators",
      icon: <Scale className="text-emerald-500" />,
      content: "Cross-border sanctions enforcement, forensic investigations, corporate intelligence, market monitoring, employee verification, IP/Threat Intelligence monitoring, Darkweb surveillance, and Social Media eKYC for suspect verification."
    },
    {
      title: "Stock Markets & Exchanges",
      icon: <TrendingUp className="text-amber-500" />,
      content: "Real-time surveillance of participants, transaction monitoring, market sentiment analysis, compliance verification, Blockchain Intelligence, employee checks, IP/Threat Intelligence monitoring, Darkweb monitoring, and Social Media eKYC for listed companies’ executives."
    },
    {
      title: "FinTech & DeFi Platforms",
      icon: <Zap className="text-violet-500" />,
      content: "Onboarding verification, crypto-asset AML, wallet risk scoring, sanctions/PEP screening, ESG Due Diligence (including supply chain), operational intelligence (Phone, IBAN, Email), employee checks, IP/Threat Intelligence monitoring, Darkweb surveillance, and Social Media eKYC for users and partners."
    },
    {
      title: "Family Offices",
      icon: <Users className="text-rose-500" />,
      content: "UBO/shareholding mapping, adverse media monitoring, ESG Due Diligence (including supply chain), market & geopolitical intelligence, counterparty verification, employee checks, IP/Threat Intelligence monitoring, Darkweb monitoring, and Social Media eKYC for portfolio oversight."
    },
    {
      title: "Market Intelligence & Political Risk Analysts",
      icon: <Globe className="text-blue-600" />,
      content: "Social sentiment & geopolitical monitoring, early-warning alerts, cross-referencing corporate intelligence, Blockchain Intelligence, ESG signals, operational intelligence, IP/Threat Intelligence monitoring, Darkweb surveillance, and Social Media eKYC for actionable intelligence reporting."
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-500 pb-32">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-100 dark:border-slate-900">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center space-x-2 text-slate-500 dark:text-slate-400 font-black text-xs uppercase tracking-widest hover:text-blue-600 dark:hover:text-blue-400 transition-all group">
            <div className="p-2 bg-slate-100 dark:bg-slate-900 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-all"><ArrowLeft size={16} /></div>
            <span>Return Home</span>
          </button>
          <div className="flex items-center space-x-3">
            <img src="assets/logo.png" alt="Minerva Logo" className="w-8 h-8 object-contain" />
            <span className="text-lg font-black tracking-tighter dark:text-white uppercase italic">Use Cases</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="pt-48 pb-24 px-6 relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center space-y-10 relative z-10">
          <div className="inline-flex items-center space-x-3 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black uppercase border border-blue-100 dark:border-blue-800/50">
            <ShieldCheck size={14} />
            <span>Institutional Applications</span>
          </div>
          <h1 className="text-6xl md:text-8xl font-black text-slate-900 dark:text-white tracking-tighter italic leading-none">
            Industry <br /> <span className="text-blue-600">Solutions.</span>
          </h1>
          <p className="text-xl text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-3xl mx-auto">
            Minerva RegTech™ provides specialized intelligence nodes tailored for the world's most demanding financial and regulatory environments.
          </p>
        </div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full opacity-5 pointer-events-none">
           <Database size={600} className="mx-auto" />
        </div>
      </header>

      {/* Use Cases Grid */}
      <main className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {useCases.map((uc, index) => (
            <div key={index} className="p-10 bg-slate-50 dark:bg-slate-900 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm group hover:bg-white dark:hover:bg-slate-950 hover:shadow-xl transition-all flex flex-col">
              <div className="p-5 bg-white dark:bg-slate-800 rounded-3xl w-fit mb-8 shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-all">
                {uc.icon}
              </div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-6 uppercase italic tracking-tighter leading-tight">
                {uc.title}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed flex-1">
                {uc.content}
              </p>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <section className="mt-40 bg-slate-900 dark:bg-blue-600 rounded-[4rem] p-12 md:p-24 text-center text-white relative overflow-hidden">
          <div className="relative z-10 space-y-10">
            <h2 className="text-4xl md:text-6xl font-black italic tracking-tighter">Ready to deploy?</h2>
            <p className="text-blue-200 dark:text-white/80 text-xl max-w-2xl mx-auto font-medium">
              Join the institutions already using Minerva to secure their operations and automate compliance.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <button 
                onClick={onBack}
                className="bg-white text-slate-900 px-12 py-6 rounded-[2rem] font-black uppercase text-xs tracking-widest hover:bg-blue-50 transition-all shadow-2xl active:scale-95 flex items-center space-x-3"
              >
                <span>Initialize Platform</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
          <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
            <Globe size={800} className="absolute -top-40 -left-40" />
          </div>
        </section>
      </main>

      <footer className="mt-40 py-12 text-center text-[10px] font-black text-slate-300 dark:text-slate-800 uppercase tracking-widest">
         © 2023 Minerva Regtech
      </footer>
    </div>
  );
};
